import type {Three as THREE} from "#material/three/exports"
import {ThreeTsl as TSL} from "#material/three/exports"

const mod289_vec4 = TSL.Fn(([x]: [x: THREE.Node<"vec4">]) => x.sub(TSL.floor(x.mul(1.0 / 289.0)).mul(289.0)))

const permute = TSL.Fn(([x]: [x: THREE.Node<"vec4">]) => mod289_vec4(x.mul(34.0).add(1.0).mul(x)))

const taylorInvSqrt = TSL.Fn(([r]: [r: THREE.Node<"vec4">]) => r.mul(-0.85373472095314).add(1.79284291400159))

const fade2 = TSL.Fn(([t]: [t: THREE.Node<"vec2">]) =>
    t
        .mul(t)
        .mul(t)
        .mul(t.mul(t.mul(6.0).sub(15.0)).add(10.0)),
)

export const threeNoise2d = TSL.Fn(([P, amplitude, pivot]: [P: THREE.Node<"vec2">, amplitude: THREE.Node<"float">, pivot: THREE.Node<"float">]) => {
    const Pi = mod289_vec4(TSL.floor(TSL.vec4(P, P)).add(TSL.vec4(0, 0, 1, 1)))

    const Pf = TSL.fract(TSL.vec4(P, P)).sub(TSL.vec4(0, 0, 1, 1))

    const ix = Pi.xzxz
    const iy = Pi.yyww
    const fx = Pf.xzxz
    const fy = Pf.yyww

    const i = permute(permute(ix).add(iy))

    let gx = TSL.fract(i.mul(1.0 / 41.0))
        .mul(2.0)
        .sub(1.0)
    const gy = TSL.abs(gx).sub(0.5)
    const tx = TSL.floor(gx.add(0.5))
    gx = gx.sub(tx)

    let g00: THREE.Node<"vec2"> = TSL.vec2(gx.x, gy.x)
    let g10: THREE.Node<"vec2"> = TSL.vec2(gx.y, gy.y)
    let g01: THREE.Node<"vec2"> = TSL.vec2(gx.z, gy.z)
    let g11: THREE.Node<"vec2"> = TSL.vec2(gx.w, gy.w)

    const norm = taylorInvSqrt(TSL.vec4(TSL.dot(g00, g00), TSL.dot(g01, g01), TSL.dot(g10, g10), TSL.dot(g11, g11)))

    g00 = g00.mul(norm.x)
    g01 = g01.mul(norm.y)
    g10 = g10.mul(norm.z)
    g11 = g11.mul(norm.w)

    const n00 = TSL.dot(g00, TSL.vec2(fx.x, fy.x))
    const n10 = TSL.dot(g10, TSL.vec2(fx.y, fy.y))
    const n01 = TSL.dot(g01, TSL.vec2(fx.z, fy.z))
    const n11 = TSL.dot(g11, TSL.vec2(fx.w, fy.w))

    const fade_xy = fade2(Pf.xy)

    const n_x = TSL.mix(TSL.vec2(n00, n01), TSL.vec2(n10, n11), fade_xy.x)
    const n_xy = TSL.mix(n_x.x, n_x.y, fade_xy.y)

    return n_xy.mul(2.3).mul(amplitude).add(pivot)
})
